import React, { memo, useRef, useState } from 'react';
import { Box, Button, HStack, Input, Pressable, Text, VStack } from 'native-base';
import DOMPurify from 'dompurify';
import { MessageMarkdown } from './MessageMarkdown';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useRouting } from '../../Common/hooks/useRouting';
import { isDoctorPortal } from '../../Common/utils/isDoctorPortal';
import { Icon } from '../../Common/components/Icon';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { graphql } from '../../__generated__';
import { useDownloadDocument } from '../../Common/hooks/useDownloadDocument';
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload';
import { useRecoilValue } from 'recoil';
import { authenticationStateAtom } from '../../Common/state/authenticationState';
import { MessagePathologyRequest } from './MessagePathologyRequest';
import { useTimeAgo } from '../../Common/hooks/useTimeAgo';
import { Webview } from '../../HeyFellaMobileApp/Common/Webview';

const MessageFileDownload = ({ fileKey }: { fileKey: any }) => {
  const [getURl, { loading }] = useLazyQuery(
    graphql(`
      query downloadThreadFile($file: String) {
        threadFileURL(file: $file)
      }
    `),
  );

  const openURL = async () => {
    const { data } = await getURl({
      variables: { file: fileKey },
    });

    if (data?.threadFileURL) {
      window.open(data?.threadFileURL);
    }
  };

  return (
    <Button
      size={'sm'}
      isLoading={loading}
      onPress={openURL}
      leftIcon={<Icon icon={faDownload} size={'sm'} color={'white'} />}
    >
      Download file
    </Button>
  );
};

export const Message = memo(
  ({
    senderID,
    ThreadID,
    channel,
    refetch,
    files,
    title,
    senderImage,
    options,
    sender,
    message,
    messageId,
    html,
    canEdit,
    sent,
    type,
    isCustomer,
  }: {
    senderID: string;
    ThreadID: string;
    channel: string;
    files: string[];
    title?: string;
    refetch: () => void;
    senderImage: React.ReactNode;
    options: string[];
    sender: string;
    message: string;
    html?: string | undefined;
    messageId: string;
    sent: string;
    type: string;
    canEdit: boolean;
    isCustomer: boolean;
  }) => {
    const { user } = useRecoilValue(authenticationStateAtom);

    const { timeAgo } = useTimeAgo(sent);

    const [editingMessage, setEditingMessage] = useState(false);

    const { navigate } = useRouting();

    const canEditMessage =
      canEdit && isDoctorPortal() && !isCustomer && senderID === user?.sub && channel === 'app';

    const ref = useRef<HTMLDivElement>(null);

    const [, setEditWidth] = useState(0);

    const [editMessage, setEditMessage] = useState<string>(message);

    const { onPressDownload, downloadingDocument } = useDownloadDocument();

    const sanitizedHtml = html ? DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true }) : undefined;

    const startEditMessage = () => {
      setEditWidth(ref?.current?.offsetWidth ?? 0);
      setEditingMessage(true);
    };

    const [editMessageMutation, { loading }] = useMutation(
      graphql(`
        mutation editMessage($content: String, $messageId: String, $threadId: String) {
          editMessage(content: $content, messageId: $messageId, threadId: $threadId) {
            id
          }
        }
      `),
    );

    const doEditMessage = async () => {
      await editMessageMutation({
        variables: {
          messageId,
          content: editMessage,
          threadId: ThreadID,
        },
      });

      setEditingMessage(false);

      await refetch();
    };

    const boxProps = {
      ...(isCustomer && {
        mr: '2%',
        w: '80%',
        ml: '18%',
      }),
      ...(!isCustomer && {
        w: '85%',
        ml: '2%',
        alignItems: 'flex-start',
      }),
      mb: 4,
    };

    const messageOptions = () => {
      if (type === 'script') {
        return (
          <Button
            onPress={() => navigate('/profile/health')}
            size={'sm'}
            isDisabled={isDoctorPortal()}
            _text={{ color: 'black' }}
            bg={'white'}
          >
            View Scripts &gt;
          </Button>
        );
      }

      if (type === 'pathologyrequest')
        return (
          <Button
            isDisabled={isDoctorPortal()}
            onPress={() => navigate(`/profile/health/pathology/${options[0]}`)}
            size={'sm'}
            _text={{ color: 'black' }}
            bg={'white'}
          >
            View Results &gt;
          </Button>
        );

      if (type === 'document') {
        return (
          <Button
            onPress={() => onPressDownload(options[0], options[1])}
            size={'sm'}
            leftIcon={<Icon icon={faDownload} size={'sm'} color={'black'} />}
            isLoading={downloadingDocument}
            _text={{ color: 'black' }}
            bg={'white'}
          >
            Download
          </Button>
        );
      }

      if (type === 'pathology') {
        return (
          <MessagePathologyRequest
            docKey={options[2]}
            consultID={options[1]}
            pathRequestID={options[0]}
          />
        );
      }

      return null;
    };

    return (
      <HStack space={2} {...boxProps}>
        {!isCustomer && senderImage}

        <VStack space={1} flex={1} alignItems={isCustomer ? 'flex-end' : 'flex-start'}>
          <HStack space={1}>
            <Text fontSize={'xs'} fontWeight={600} color="messagingBubbleHeader">
              {title} {sender}{' '}
              <Text fontWeight={'normal'} color={'messagingBubbleFooter'}>
                {timeAgo}
              </Text>
              {canEditMessage && !editingMessage && (
                <Pressable ml={2} onPress={startEditMessage}>
                  <Icon icon={faPencil} color={'gray'} size={'sm'} />
                </Pressable>
              )}
            </Text>
          </HStack>

          <VStack
            ref={ref}
            space={2}
            bg={isCustomer ? 'messagingBubbleAlt' : 'messagingBubble'}
            p={3}
            py={1}
            borderRadius="xl"
            color="messagingBubbleText"
          >
            {!editingMessage && <MessageMarkdown content={message} />}

            {sanitizedHtml && (
              <Box w={'100%'} h={600} bg={'white'}>
                <Webview srcDoc={sanitizedHtml} />
              </Box>
            )}

            {editingMessage && (
              <Box
                style={{
                  width: 200,
                }}
                h={300}
                position={'relative'}
              >
                <Input
                  _focus={{
                    bg: 'white',
                  }}
                  editable={!loading}
                  flex={1}
                  bg={'white'}
                  multiline={true}
                  value={editMessage}
                  onChangeText={(text) => setEditMessage(text)}
                />

                <Button onPress={doEditMessage} isLoading={loading} size={'sm'} mt={2}>
                  Edit Message
                </Button>
              </Box>
            )}

            {!editingMessage && messageOptions()}

            {files?.map((file) => (
              <MessageFileDownload fileKey={file} key={file} />
            ))}
          </VStack>
        </VStack>

        {isCustomer && senderImage}
      </HStack>
    );
  },
);
